import React, { useState } from "react";
import { Flex, Modal, Type } from "mesh-component-library";
import { ClockGlyph, GlobeGlyph, HandsetGlyph, LocationPinGlyph } from "mesh-icon-library";
import { GoogleDestinationWithPlaceId, ServiceDetailsBreakdown } from "../../../../../types";
import { CommunityText } from "../../LocationCard/LocationCard.styles";
import StarRating from "../../../../../components/starrating";
import {
  ColumnFlex,
  HorizontalScrollbox,
  SpaceBetweenColumnFlex,
  SpaceBetweenFlex,
} from "../DetailedLocationReviewV2.styles";

export type GoogleLocationDisplayProps = {
  location: GoogleDestinationWithPlaceId;
  towServiceDetails: ServiceDetailsBreakdown;
};

export const GoogleLocationDisplay = ({ location, towServiceDetails }: GoogleLocationDisplayProps) => {
  const locationHasPhotos = location.photos && location.photos.length > 0;
  const [hasImgError, setHasImgError] = useState<boolean>(false);
  const hasExtraPhotos = locationHasPhotos && location.photos?.length > 1;

  const formatAddressForDisplay = () => {
    const street = location.addressDetails.addressLine1 || "";
    const city = location.addressDetails.city || "";
    const state = location.addressDetails.state || "";
    const zipCode = location.addressDetails.zipCode || "";

    return `${street} ${city}, ${state.toLocaleUpperCase()} ${zipCode}`;
  };

  const renderExtraPhotos = () => {
    const extraPhotos = location.photos.slice(1, 3);

    return extraPhotos.map((photo) => (
      <img
        key={`photo-${photo.url}`}
        src={`${photo.getUrl({ maxHeight: 500 })}`}
        alt="Google provided photo of shop"
        style={{ width: "100%", maxHeight: "15vh", height: "auto" }}
        referrerPolicy="no-referrer"
        data-testId="detailedLocationReviewV2-extra-photo"
      />
    ));
  };

  const handleError = () => {
    setHasImgError(true);
  };

  return (
    <>
      {/* Distance & Name */}
      <Modal.Header
        subTitle={
          <Type
            size="xl"
            weight="bold"
          >{`${location.towInfo.distanceFromCurrentLocation} ${location.towInfo.unitsShorthand}`}</Type>
        }
        title={
          <Type size="4xl" weight="bold" variant="default">
            {location.name}
          </Type>
        }
        data-testid="detailedLocationReviewV2-modalTitle"
        variant="information"
        style={{ padding: "0 1rem" }}
      />
      {/* Address & Rating */}
      <Modal.Subheader style={{ paddingRight: "16px" }}>
        <Type weight="bold" size="sm" data-testid="detailedLocationReviewV2-locationAddress">
          {formatAddressForDisplay()}
        </Type>
        {location.rating ? (
          <CommunityText align="left" size="2xs" data-testid="locationCard-rating">
            <StarRating
              starRating={location.rating.toString()}
              baseColor="orange"
              reviews={location.reviews.length.toString()}
            />
          </CommunityText>
        ) : null}
      </Modal.Subheader>
      {/* Cost breakdown */}
      <Modal.Subheader style={{ paddingRight: "16px" }}>
        <SpaceBetweenFlex>
          <Type weight="thin">{`Base rate up to ${towServiceDetails.towIncludedDistanceQty} miles`}</Type>
          <Type weight="thin" data-testId="detailedLocationReviewV2-baseRateCost">{`$${towServiceDetails.displayCost}.00`}</Type>
        </SpaceBetweenFlex>
        <SpaceBetweenFlex>
          <Type weight="thin">{`${
            location.towInfo.overMileageDistance
          } additional miles at $${towServiceDetails.costPerMile.toFixed(2)}/mile`}</Type>
          <Type weight="thin">{`$${location.towInfo.overMileageCost}`}</Type>
        </SpaceBetweenFlex>
      </Modal.Subheader>
      <Modal.Subheader hasBorder={false} style={{ paddingRight: "16px" }}>
        <SpaceBetweenFlex>
          <Type weight="bold">Amount due</Type>
          <Type weight="bold">{`$${location.towInfo.totalTowCost}`}</Type>
        </SpaceBetweenFlex>
      </Modal.Subheader>
      <Modal.Body style={{ paddingLeft: "0px", paddingRight: "0px" }}>
        <SpaceBetweenColumnFlex>
          {/* Main picture */}
          <div style={{ width: "100%" }}>
            {locationHasPhotos && !hasImgError && location.photos?.length ? (
              <img
                src={location.photos[0].getUrl({ maxHeight: 500 })}
                alt="Google provided photo of shop"
                style={{ width: "100%", maxHeight: "15vh", height: "auto" }}
                onError={handleError}
                referrerPolicy="no-referrer"
              />
            ) : null}
          </div>
          {/* Address, website, phone #, hours */}
          <ColumnFlex>
            <Flex alignItems="middle">
              <LocationPinGlyph style={{ width: "18px", height: "24px" }} />
              <Type size="xs" utils={{ hrLeft: "md" }}>
                {formatAddressForDisplay()}
              </Type>
            </Flex>
            {!!location.website && (
              <Flex alignItems="middle" style={{ flexWrap: "nowrap" }}>
                <GlobeGlyph style={{ width: "18px", height: "24px", flexShrink: "0"  }} />
                <Type
                  size="xs"
                  utils={{ hrLeft: "md" }}
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  <a href={`${location.website}`}>{location.website}</a>
                </Type>
              </Flex>
            )}
            {!!location.formatted_phone_number && (
              <Flex alignItems="middle">
                <HandsetGlyph style={{ width: "18px", height: "24px" }} />
                <a href={`tel:${location.formatted_phone_number}`} data-testid="detailedLocationReviewV2-phoneNumber">
                  <Type size="xs" utils={{ hrLeft: "md" }}>
                    {location.formatted_phone_number}
                  </Type>
                </a>
              </Flex>
            )}
            {!!location.hoursToDisplay && (
              <Flex alignItems="middle">
                <ClockGlyph style={{ width: "18px", height: "24px" }} />
                <Type size="xs" utils={{ hrLeft: "md" }}>
                  {location.hoursToDisplay}
                </Type>
              </Flex>
            )}
          </ColumnFlex>
          {/* Extra images */}
          {locationHasPhotos && (hasExtraPhotos || !hasImgError) ? (
            <HorizontalScrollbox>{renderExtraPhotos()}</HorizontalScrollbox>
          ) : (
            <div></div>
          )}
        </SpaceBetweenColumnFlex>
      </Modal.Body>
    </>
  );
};
